import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const TYPE_LABEL = {
  road: "Road",
  osr: "OSR",
  footpath: "Footpath",
  dumping_yard: "Dumping Yard",
  sipcot_po_office: "Project Office",
};

function toDdMmYyyy(iso) {
  if (!iso) return "—";
  const [y, m, d] = String(iso).split("-");
  if (!y || !m || !d) return iso;
  return `${d}.${m}.${y}`;
}

export function AssetHistoryDialog({ assetId, open, onOpenChange }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!open || !assetId) return;
    let cancelled = false;
    setLoading(true);
    api.get(`/admin/cleaning-cycles/asset/${assetId}/history?days=90`)
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, assetId]);

  const asset = data?.asset;
  const days = data?.days || [];
  const photos = data?.photos || [];
  // Heatmap: 13 weeks × 7 days (most recent on the right)
  const cells = [...days];
  // Pad to multiple of 7
  while (cells.length % 7 !== 0) cells.unshift({ visits: 0 });
  const maxKm = Math.max(0.1, ...days.map((d) => d.km_cleaned || 0));
  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="asset-history-dialog">
        <DialogHeader>
          <DialogTitle>{asset?.name || "Asset history"}</DialogTitle>
          <DialogDescription>
            {asset ? (
              <span className="flex flex-wrap items-center gap-2">
                <Badge variant="outline">{TYPE_LABEL[asset.asset_type] || asset.asset_type}</Badge>
                {asset.length_km ? <Badge variant="outline">{Number(asset.length_km).toFixed(1)} km</Badge> : null}
                {asset.phase ? <Badge variant="outline">{asset.phase}</Badge> : null}
              </span>
            ) : ""}
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex h-40 items-center justify-center text-slate-500"><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…</div>
        ) : (
          <div className="space-y-5">
            {/* Heatmap */}
            <div>
              <div className="mb-2 flex items-center justify-between text-xs text-slate-500">
                <span className="uppercase tracking-wider">Last 90 days · daily activity</span>
                <span className="flex items-center gap-1">
                  <span className="h-2 w-3 rounded-sm bg-slate-200" />
                  <span className="h-2 w-3 rounded-sm bg-amber-200" />
                  <span className="h-2 w-3 rounded-sm bg-amber-400" />
                  <span className="h-2 w-3 rounded-sm bg-emerald-500" />
                </span>
              </div>
              <div className="flex gap-1" data-testid="asset-history-calendar-heatmap">
                {weeks.map((w, i) => (
                  <div key={i} className="flex flex-col gap-1">
                    {w.map((d, j) => {
                      const k = (d.km_cleaned || 0) / maxKm;
                      const tone = d.visits > 0 ? (k > 0.66 ? "bg-emerald-500" : k > 0.33 ? "bg-amber-400" : "bg-amber-200") : "bg-slate-100";
                      return <div key={j} title={`${toDdMmYyyy(d.date)} · ${d.visits || 0} visit(s) · ${(d.km_cleaned||0).toFixed(2)} km`} className={`h-3 w-3 rounded-sm ${tone}`} />;
                    })}
                  </div>
                ))}
              </div>
            </div>
            {/* Photos */}
            {photos.length > 0 ? (
              <div>
                <div className="mb-2 text-xs uppercase tracking-wider text-slate-500">Recent captures ({photos.length})</div>
                <div className="flex flex-wrap gap-2" data-testid="asset-history-photos-carousel">
                  {photos.slice(0, 12).map((p, i) => (
                    <div key={i} className="flex flex-col items-center gap-1">
                      <img src={p.photo_b64} alt="" className="h-20 w-20 rounded-md border object-cover" />
                      <div className="text-[10px] text-slate-500">{toDdMmYyyy(p.date)}</div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-sm text-slate-500">No captures in the last 90 days.</div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
